// Document Builder Hybrid Service - Works with both API and localStorage
// This service provides fallback to localStorage when API endpoints are not available
import { api } from './api';
import uploadService from './upload.service';

class DocumentBuilderHybridService {
    // Base URL for document API endpoints
    baseURL = '/api/documents';

    // Check if API is available
    apiAvailable = false;

    constructor() {
        this.checkAPIAvailability();
    }

    // Check if API endpoints are available
    async checkAPIAvailability() {
        try {
            await api.get('/api/health');
            this.apiAvailable = true;
        } catch (error) {
            console.log('API not available, using localStorage fallback');
            this.apiAvailable = false;
        }
    }

    // ==================== DOCUMENT TEMPLATES ====================

    /**
     * Fetch all document templates
     */
    async getDocumentTemplates() {
        try {
            const response = await api.get(`${this.baseURL}/templates`);
            return response.data.templates || [];
        } catch (error) {
            // Fallback to localStorage
            console.log('Using localStorage for templates');
            const templates = JSON.parse(localStorage.getItem('documentTemplates') || '[]');
            return templates;
        }
    }

    /**
     * Save selected document templates
     */
    async saveSelectedTemplates(templates) {
        try {
            const response = await api.post(`${this.baseURL}/templates/selected`, {
                templates
            });
            // Also save to localStorage for offline access
            localStorage.setItem('selectedDocumentTemplates', JSON.stringify(templates));
            return response.data;
        } catch (error) {
            // Fallback to localStorage
            console.log('Saving templates to localStorage');
            localStorage.setItem('selectedDocumentTemplates', JSON.stringify(templates));
            return { success: true, templates };
        }
    }

    /**
     * Get selected templates
     */
    async getSelectedTemplates() {
        try {
            const response = await api.get(`${this.baseURL}/templates/selected`);
            return response.data.templates || {};
        } catch (error) {
            // Fallback to localStorage
            const templates = JSON.parse(localStorage.getItem('selectedDocumentTemplates') || '{}');
            return templates;
        }
    }

    // ==================== DOCUMENT GENERATION ====================

    /**
     * Generate a new document
     */
    async generateDocument(documentData) {
        try {
            const response = await api.post(`${this.baseURL}/generate`, {
                category: documentData.category,
                templateId: documentData.templateId,
                templateName: documentData.templateName,
                content: documentData.content,
                isDraft: documentData.isDraft || false
            });

            // Also save to localStorage
            this.saveToLocalStorage('generatedDocuments', response.data.document);
            return response.data.document;
        } catch (error) {
            // Fallback to localStorage
            console.log('Saving document to localStorage');
            const document = {
                ...documentData,
                id: documentData.id || Date.now().toString(),
                generatedAt: documentData.generatedAt || new Date().toISOString()
            };

            this.saveToLocalStorage('generatedDocuments', document);
            return document;
        }
    }

    /**
     * Update existing document
     */
    async updateDocument(documentId, updates) {
        try {
            const response = await api.put(`${this.baseURL}/${documentId}`, updates);
            this.updateInLocalStorage('generatedDocuments', documentId, response.data.document);
            return response.data.document;
        } catch (error) {
            // Fallback to localStorage
            console.log('Updating document in localStorage');
            const updated = this.updateInLocalStorage('generatedDocuments', documentId, updates);
            return updated;
        }
    }

    /**
     * Delete document
     */
    async deleteDocument(documentId) {
        try {
            const response = await api.delete(`${this.baseURL}/${documentId}`);
            this.deleteFromLocalStorage('generatedDocuments', documentId);
            return response.data;
        } catch (error) {
            // Fallback to localStorage
            console.log('Deleting document from localStorage');
            this.deleteFromLocalStorage('generatedDocuments', documentId);
            return { success: true };
        }
    }

    // ==================== DOCUMENT DRAFTS ====================

    /**
     * Save document as draft
     */
    async saveDraft(documentData) {
        try {
            const response = await api.post(`${this.baseURL}/drafts`, {
                ...documentData,
                isDraft: true
            });
            this.saveToLocalStorage('draftDocuments', response.data.draft);
            return response.data.draft;
        } catch (error) {
            // Fallback to localStorage
            console.log('Saving draft to localStorage');
            const draft = {
                ...documentData,
                id: documentData.id || Date.now().toString(),
                isDraft: true,
                lastModified: new Date().toISOString()
            };

            this.saveToLocalStorage('draftDocuments', draft);
            return draft;
        }
    }

    /**
     * Get all drafts
     */
    async getDrafts() {
        try {
            const response = await api.get(`${this.baseURL}/drafts`);
            return response.data.drafts || [];
        } catch (error) {
            // Fallback to localStorage
            const drafts = JSON.parse(localStorage.getItem('draftDocuments') || '[]');
            return drafts;
        }
    }

    /**
     * Delete draft
     */
    async deleteDraft(draftId) {
        try {
            const response = await api.delete(`${this.baseURL}/drafts/${draftId}`);
            this.deleteFromLocalStorage('draftDocuments', draftId);
            return response.data;
        } catch (error) {
            // Fallback to localStorage
            this.deleteFromLocalStorage('draftDocuments', draftId);
            return { success: true };
        }
    }

    // ==================== DOCUMENT DISTRIBUTION ====================

    /**
     * Send document to employee(s)
     */
    async sendDocument(documentId, recipientData) {
        try {
            const response = await api.post(`${this.baseURL}/${documentId}/send`, {
                employeeId: recipientData.employeeId,
                employeeEmail: recipientData.employeeEmail,
                sendViaEmail: recipientData.sendViaEmail || false,
                sendViaPortal: recipientData.sendViaPortal || true,
                metadata: {
                    sentBy: recipientData.sentBy || 'HR Department',
                    sentAt: new Date().toISOString(),
                    department: recipientData.department
                }
            });

            // Also save to employee's localStorage key (API success path)
            // Get the document first to have full data
            const documents = JSON.parse(localStorage.getItem('generatedDocuments') || '[]');
            const document = documents.find(d => d.id === documentId);

            if (document) {
                const sentDoc = {
                    ...document,
                    sentTo: recipientData.employeeId,
                    sentToEmail: recipientData.employeeEmail,
                    sentToName: recipientData.employeeName,
                    sentBy: recipientData.sentBy || 'HR Department',
                    sentAt: new Date().toISOString(),
                    status: 'sent',
                    type: 'company',
                    category: 'company'
                };

                // Save to all the same locations as fallback path
                this.saveToEmployeeDocuments(recipientData.employeeId, sentDoc);
                this.saveToLocalStorage(`employeeCompanyDocuments_${recipientData.employeeId}`, sentDoc);
                this.saveToUniversalPool(sentDoc);
                this.saveToLocalStorage('allCompanyDocuments', sentDoc);

                console.log('API SUCCESS: Document saved for employee:', {
                    employeeId: recipientData.employeeId,
                    employeeEmail: recipientData.employeeEmail,
                    documentId: document.id,
                    documentName: document.templateName
                });
            }
            return response.data;
        } catch (error) {
            // Fallback to localStorage
            console.log('Saving document distribution to localStorage');

            // Get the document
            const documents = JSON.parse(localStorage.getItem('generatedDocuments') || '[]');
            const document = documents.find(d => d.id === documentId);

            if (document) {
                // Save to employee-specific key
                const sentDoc = {
                    ...document,
                    sentTo: recipientData.employeeId,
                    sentToEmail: recipientData.employeeEmail,
                    sentToName: recipientData.employeeName,
                    sentBy: recipientData.sentBy || 'HR Department',
                    sentAt: new Date().toISOString(),
                    status: 'sent',
                    type: 'company', // Mark as company document for employee
                    category: 'company' // Ensure it appears in Company Documents tab
                };

                // Save to employee-specific documents
                this.saveToEmployeeDocuments(recipientData.employeeId, sentDoc);

                // Also save to employee's company documents pool
                this.saveToLocalStorage(`employeeCompanyDocuments_${recipientData.employeeId}`, sentDoc);

                // Also save to universal document pools for cross-user access
                this.saveToUniversalPool(sentDoc);

                // Also save to company documents pool
                this.saveToLocalStorage('allCompanyDocuments', sentDoc);

                console.log('Document saved for employee:', {
                    employeeId: recipientData.employeeId,
                    employeeEmail: recipientData.employeeEmail,
                    documentId: document.id,
                    documentName: document.templateName,
                    savedTo: [
                        `employeeDocuments_${recipientData.employeeId}`,
                        `employeeCompanyDocuments_${recipientData.employeeId}`,
                        'sharedDocumentPool',
                        'allCompanyDocuments'
                    ]
                });

                // Verify document was saved
                setTimeout(() => {
                    const verifyDoc = JSON.parse(localStorage.getItem(`employeeCompanyDocuments_${recipientData.employeeId}`) || '[]');
                    console.log(`Verification - Documents in employeeCompanyDocuments_${recipientData.employeeId}:`, verifyDoc);
                }, 100);

                return { success: true, document: sentDoc };
            }

            return { success: false, error: 'Document not found' };
        }
    }

    /**
     * Get document status
     */
    async getDocumentStatus(documentId) {
        try {
            const response = await api.get(`${this.baseURL}/${documentId}/status`);
            return response.data.status;
        } catch (error) {
            // Fallback to localStorage
            const documents = JSON.parse(localStorage.getItem('generatedDocuments') || '[]');
            const document = documents.find(d => d.id === documentId);
            return document?.status || 'unknown';
        }
    }

    // ==================== DOCUMENT RETRIEVAL ====================

    /**
     * Get all generated documents (admin view)
     */
    async getGeneratedDocuments() {
        try {
            const response = await api.get(`${this.baseURL}/generated`);
            return response.data.documents || [];
        } catch (error) {
            // Fallback to localStorage
            const documents = JSON.parse(localStorage.getItem('generatedDocuments') || '[]');
            return documents;
        }
    }

    /**
     * Get documents for specific employee
     */
    async getEmployeeDocuments(employeeId) {
        try {
            const response = await api.get(`${this.baseURL}/employee/${employeeId}`);
            return response.data.documents || [];
        } catch (error) {
            // Fallback to localStorage - check multiple sources
            const sources = [
                `employeeDocuments_${employeeId}`,
                `employeeCompanyDocuments_${employeeId}`,
                'sharedDocumentPool',
                'universalCompanyDocuments',
                'allCompanyDocuments',
                'allReceivedDocuments'
            ];

            let allDocs = [];
            const docMap = new Map();

            sources.forEach(source => {
                const docs = JSON.parse(localStorage.getItem(source) || '[]');
                if (Array.isArray(docs)) {
                    docs.forEach(doc => {
                        if (doc && doc.id && !docMap.has(doc.id)) {
                            // Check if document is for this employee or is company-wide
                            if (!doc.sentTo || doc.sentTo === employeeId || doc.type === 'company') {
                                docMap.set(doc.id, doc);
                            }
                        }
                    });
                }
            });

            return Array.from(docMap.values());
        }
    }

    /**
     * Get company documents (visible to all employees)
     */
    async getCompanyDocuments() {
        try {
            const response = await api.get(`${this.baseURL}/company`);
            return response.data.documents || [];
        } catch (error) {
            // Fallback to localStorage - get from universal pools
            const sources = [
                'universalCompanyDocuments',
                'sharedDocumentPool',
                'allCompanyDocuments',
                'allReceivedDocuments'
            ];

            const docMap = new Map();

            sources.forEach(source => {
                const docs = JSON.parse(localStorage.getItem(source) || '[]');
                if (Array.isArray(docs)) {
                    docs.forEach(doc => {
                        if (doc && doc.id && !docMap.has(doc.id)) {
                            if (doc.type === 'company' || doc.sentBy) {
                                docMap.set(doc.id, doc);
                            }
                        }
                    });
                }
            });

            return Array.from(docMap.values());
        }
    }

    /**
     * Get my documents (for logged-in employee)
     */
    async getMyDocuments() {
        try {
            const response = await api.get(`${this.baseURL}/my-documents`);
            return {
                received: response.data.received || [],
                uploaded: response.data.uploaded || [],
                company: response.data.company || []
            };
        } catch (error) {
            // Fallback to localStorage
            const user = JSON.parse(localStorage.getItem('user') || '{}');
            const employeeId = user.id;

            // Get documents from various sources
            const employeeDocs = await this.getEmployeeDocuments(employeeId);
            const companyDocs = await this.getCompanyDocuments();

            // Separate by type
            const received = employeeDocs.filter(d => d.sentTo === employeeId && d.type === 'received');
            const uploaded = employeeDocs.filter(d => d.uploadedBy === user.name || d.uploadedBy === user.email);
            const company = [...companyDocs, ...employeeDocs.filter(d => d.type === 'company')];

            return { received, uploaded, company };
        }
    }

    // ==================== FIREBASE STORAGE (With Fallback) ====================

    /**
     * Upload document to Firebase Storage
     */
    async uploadToFirebase(documentFile, metadata = {}) {
        try {
            const formData = new FormData();
            formData.append('document', documentFile);
            formData.append('metadata', JSON.stringify(metadata));

            const response = await api.post(`${this.baseURL}/upload`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });

            return {
                url: response.data.url,
                path: response.data.path,
                documentId: response.data.documentId
            };
        } catch (error) {
            // Fallback - create a local URL using FileReader
            console.log('Firebase unavailable, using local storage');

            return new Promise((resolve) => {
                const reader = new FileReader();
                reader.onloadend = () => {
                    const documentId = 'local_' + Date.now();
                    const document = {
                        id: documentId,
                        name: documentFile.name,
                        dataUrl: reader.result,
                        metadata,
                        uploadedAt: new Date().toISOString()
                    };

                    // Save to localStorage
                    const uploads = JSON.parse(localStorage.getItem('uploadedDocuments') || '[]');
                    uploads.push(document);
                    localStorage.setItem('uploadedDocuments', JSON.stringify(uploads));

                    resolve({
                        url: reader.result,
                        path: 'local/' + documentId,
                        documentId
                    });
                };
                reader.readAsDataURL(documentFile);
            });
        }
    }

    /**
     * Generate PDF from document content and upload
     */
    async generateAndUploadPDF(documentData) {
        try {
            const response = await api.post(`${this.baseURL}/generate-pdf`, {
                content: documentData.content,
                templateName: documentData.templateName,
                employeeName: documentData.employeeName,
                metadata: documentData.metadata
            });

            return {
                pdfUrl: response.data.pdfUrl,
                firebasePath: response.data.firebasePath,
                documentId: response.data.documentId
            };
        } catch (error) {
            // Fallback - just return without saving
            console.log('PDF generation unavailable, will save document content directly');

            const documentId = 'local_pdf_' + Date.now();

            return {
                pdfUrl: null,
                firebasePath: null,
                documentId
            };
        }
    }

    /**
     * Get document download URL
     */
    async getDocumentUrl(documentId) {
        try {
            const response = await api.get(`${this.baseURL}/${documentId}/url`);
            return response.data.url;
        } catch (error) {
            // Fallback - check local uploads
            const uploads = JSON.parse(localStorage.getItem('uploadedDocuments') || '[]');
            const document = uploads.find(d => d.id === documentId);
            return document?.dataUrl || null;
        }
    }

    /**
     * Delete document from Firebase Storage
     */
    async deleteFromFirebase(documentPath) {
        try {
            const response = await api.delete(`${this.baseURL}/storage`, {
                data: { path: documentPath }
            });
            return response.data;
        } catch (error) {
            // Fallback - remove from local storage
            if (documentPath && documentPath.startsWith('local/')) {
                const documentId = documentPath.replace('local/', '');
                const uploads = JSON.parse(localStorage.getItem('uploadedDocuments') || '[]');
                const filtered = uploads.filter(d => d.id !== documentId);
                localStorage.setItem('uploadedDocuments', JSON.stringify(filtered));
            }
            return { success: true };
        }
    }

    // ==================== SEARCH & FILTERS ====================

    /**
     * Search documents
     */
    async searchDocuments(query, filters = {}) {
        try {
            const response = await api.post(`${this.baseURL}/search`, {
                query,
                ...filters
            });
            return response.data.documents || [];
        } catch (error) {
            // Fallback - search in localStorage
            const documents = await this.getMyDocuments();
            const allDocs = [...documents.received, ...documents.uploaded, ...documents.company];

            const searchLower = query.toLowerCase();
            return allDocs.filter(doc => {
                const matchesQuery = !query ||
                    doc.templateName?.toLowerCase().includes(searchLower) ||
                    doc.category?.toLowerCase().includes(searchLower) ||
                    doc.content?.toLowerCase().includes(searchLower);

                const matchesType = !filters.type || doc.type === filters.type;
                const matchesEmployee = !filters.employeeId || doc.sentTo === filters.employeeId;

                return matchesQuery && matchesType && matchesEmployee;
            });
        }
    }

    // ==================== HELPER METHODS ====================

    /**
     * Save to localStorage with deduplication
     */
    saveToLocalStorage(key, document) {
        const existing = JSON.parse(localStorage.getItem(key) || '[]');
        const index = existing.findIndex(d => d.id === document.id);

        if (index >= 0) {
            existing[index] = document;
        } else {
            existing.push(document);
        }

        localStorage.setItem(key, JSON.stringify(existing));
    }

    /**
     * Update document in localStorage
     */
    updateInLocalStorage(key, documentId, updates) {
        const existing = JSON.parse(localStorage.getItem(key) || '[]');
        const index = existing.findIndex(d => d.id === documentId);

        if (index >= 0) {
            existing[index] = { ...existing[index], ...updates };
            localStorage.setItem(key, JSON.stringify(existing));
            return existing[index];
        }

        return null;
    }

    /**
     * Delete from localStorage
     */
    deleteFromLocalStorage(key, documentId) {
        const existing = JSON.parse(localStorage.getItem(key) || '[]');
        const filtered = existing.filter(d => d.id !== documentId);
        localStorage.setItem(key, JSON.stringify(filtered));
    }

    /**
     * Save to employee-specific documents
     */
    saveToEmployeeDocuments(employeeId, document) {
        const key = `employeeDocuments_${employeeId}`;
        this.saveToLocalStorage(key, document);
    }

    /**
     * Save to universal document pools
     */
    saveToUniversalPool(document) {
        const pools = [
            'sharedDocumentPool',
            'universalCompanyDocuments',
            'allReceivedDocuments'
        ];

        pools.forEach(pool => {
            this.saveToLocalStorage(pool, document);
        });
    }
}

export default new DocumentBuilderHybridService();